import { TriangleAlert } from 'lucide-react';
import { useId, useState } from 'react';
import { isApiError } from '../../api/client';
import type { Network } from '../../api/types';
import { Button, Dialog } from '../../components/ui';
import { strings } from '../../strings';
import { networkErrorInfo } from '../shared';

const p = strings.networks.page;

function TypedName({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  const id = useId();
  return (
    <div className="mt-3">
      <label className="label" htmlFor={id}>
        {label}
      </label>
      <input
        id={id}
        className="input"
        value={value}
        autoComplete="off"
        spellCheck={false}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

/** "Destroy <network>" (spec 11 6.15.4): what it deletes, that the addresses stay, and the typed name. */
export function DestroyNetworkDialog({
  network,
  busy,
  onClose,
  onConfirm,
}: {
  network: Network;
  busy: boolean;
  onClose: () => void;
  onConfirm: () => void;
}) {
  const [typed, setTyped] = useState('');
  return (
    <Dialog
      title={p.destroyTitle(network.name)}
      onClose={onClose}
      className="dialog-sheet"
      footer={
        <>
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          <Button variant="danger" disabled={busy || typed !== network.name} onClick={onConfirm}>
            {p.destroySubmit(network.name)}
          </Button>
        </>
      }
    >
      <p className="callout callout-warning mb-3" data-testid="destroy-network-text">
        <TriangleAlert aria-hidden className="size-4 shrink-0" />
        {p.destroyText(network.name)}
      </p>
      <TypedName label={p.typeToConfirm(network.name)} value={typed} onChange={setTyped} />
    </Dialog>
  );
}

/** Delete (only `new` or `destroyed` without attachments): a confirmation; its 409s read item 18's sentences. */
export function DeleteNetworkDialog({
  network,
  busy,
  error,
  onClose,
  onConfirm,
  onShowAttachments,
}: {
  network: Network;
  busy: boolean;
  error: unknown;
  onClose: () => void;
  onConfirm: () => void;
  onShowAttachments: () => void;
}) {
  const info =
    error && isApiError(error)
      ? networkErrorInfo(error.code, { network: network.name, message: error.detail })
      : null;
  return (
    <Dialog
      title={p.deleteTitle(network.name)}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>{strings.common.cancel}</Button>
          <Button variant="danger" disabled={busy} onClick={onConfirm} data-autofocus>
            {strings.common.delete}
          </Button>
        </>
      }
    >
      <p>{p.deleteText(network.name)}</p>
      {error ? (
        <div role="alert" className="callout callout-danger mt-3">
          <TriangleAlert aria-hidden className="size-4 shrink-0" />
          <div className="min-w-0 flex-1">
            {info?.sentence ?? (error instanceof Error ? error.message : String(error))}
          </div>
          {info?.action === 'show-attachments' && (
            <Button className="btn-sm" onClick={onShowAttachments}>
              {strings.networks.actions.showAttachments}
            </Button>
          )}
        </div>
      ) : null}
    </Dialog>
  );
}
